import {
  DigitalDataEvent,
  MetricsTrackerConfigurationType,
  MetricsTrackerConfigurationUserType,
  MetricsTrackerConfigurationUserUpdateableType,
} from '../types/types';
import { parseOneTrustCookie } from '../utils';

const ONETRUST_FUNCTIONAL_COOKIE_GROUP = 'C0003';

const blockedByOneTrust = (overrideOneTrust = false) => {
  const { hasOneTrustCookie, groups: oneTrustGroups } = parseOneTrustCookie();

  // we are blocked by OneTrust if...
  return (
    // we're not overridden to _ignore_ OneTrust...
    !overrideOneTrust &&
    // the OneTrust cookie is not present or does not have
    // functional cookies accepted
    !(
      hasOneTrustCookie &&
      oneTrustGroups[ONETRUST_FUNCTIONAL_COOKIE_GROUP] === true
    )
  );
};

const conditionallyAddKey = (
  keyName: string,
  value: any,
  out: Record<string, any>
) => {
  if (value) {
    // the whole point of this method is to populate a single object with props,
    // so passing that object around by reference is the most performant
    // eslint-disable-next-line no-param-reassign
    out[keyName] = value;
  }
};

const hydrateDefaultUserProps = (
  user: MetricsTrackerConfigurationUserType &
    MetricsTrackerConfigurationUserUpdateableType
): Record<string, string | undefined> => {
  const userProps = {};
  conditionallyAddKey('languageId', user?.languageId, userProps);
  conditionallyAddKey('userPackageId', user?.package, userProps);
  conditionallyAddKey('userPackageName', user?.packageName, userProps);
  conditionallyAddKey(
    'userSubscriptionStatus',
    user?.subscriptionStatus,
    userProps
  );
  conditionallyAddKey('dateJoined', user?.joinedAt, userProps);
  conditionallyAddKey('dateSubscribed', user?.subscribedAt, userProps);
  conditionallyAddKey('userRole', user.group?.membership?.type, userProps);
  return userProps;
};

export default function amplitudeSubscriber(
  digitalDataEvent: DigitalDataEvent,
  config: MetricsTrackerConfigurationType
) {
  const { amplitudeToken, user, overrideOneTrust } = config;
  const { data: digitalData } = digitalDataEvent;
  const {
    amplitudeEvent,
    amplitudeUserProperties,
    actionType,
    actionFlow,
    ...data
  } = digitalData;

  if (
    // bail if we have no ampltude token...
    !amplitudeToken ||
    // ...or if this is not an amplitude event...
    !amplitudeEvent ||
    // ...or if the user is being impersonated by an admin...
    user?.session?.isAdminImpersonation ||
    // ...or if OneTrust functional cookies haven't been accepted
    blockedByOneTrust(overrideOneTrust)
  ) {
    return;
  }

  const { id: userId, isAuthenticated, group, email } = user;

  // we'll include the amplitude SDK here via "require" to avoid getting
  // yelled at by the test runner since amplitude makes explicit references
  // to "window", which doesn't exist in the test environment before we mock it
  // eslint-disable-next-line global-require
  const amplitudeRequire = require('amplitude-js');

  // this is to catch a bundling issue that was introduced with amplitude-js@8.11.0
  // that used the ESM module instead of the default `main` import:
  // https://github.com/amplitude/Amplitude-JavaScript/commit/3e98d506f003ba428ab08796d1246ccb387d1f32
  const amplitude = amplitudeRequire.default ?? amplitudeRequire;

  try {
    const amplitudeInstance = amplitude.getInstance();
    amplitudeInstance.init(amplitudeToken, null, { includeReferrer: true });
    amplitudeInstance.setTransport('beacon');

    if (isAuthenticated) {
      // if the user belongs to a team, send that down as a group property
      // set this before the user props below so that group info is sent as part
      // of the user props call
      if (group?.membership?.groupId) {
        amplitudeInstance.setGroup('teamId', group?.membership?.groupId);
      }

      amplitudeInstance.setUserId(userId);

      // add in any core props if they're present on the user object
      let userProps = hydrateDefaultUserProps(user);

      // mix in any supplied user props
      if (
        Object.prototype.toString.call(amplitudeUserProperties) ===
        '[object Object]'
      ) {
        userProps = { ...userProps, ...amplitudeUserProperties };
      }

      if (email) {
        const [, emailDomain] = email.split('@');

        // make extremely sure that we've got a real email and will only be sending the domain
        if (emailDomain && emailDomain.indexOf('.') > -1) {
          userProps.emailDomain = emailDomain;
        }
      }

      amplitudeInstance.setUserProperties(userProps);
    } else {
      // make sure we've cleared out the data for any previous logged in user
      amplitudeInstance.clearStorage();
    }

    amplitudeInstance.logEvent(amplitudeEvent, { ...data });
  } catch (err) {
    // silently fail; we don't want a bad metrics call taking the whole experience down
  }
}
